import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import Button from '@/components/ui/Button';
import { CASE_POSTS } from '@/data/mockData';
import w from './SafetyEnv.module.scss';
import { GUIDE_ICON } from '@/components/GuideIcons';

// ============================================================================
//  안전 — 안전 솔루션 (랜딩)  (/safety)
//  [2026-10-11 사용자] '화이트 airy' 리뉴얼 1호 — 목표 디자인 evs.com/company/brand-resources.
//    흰 배경 · 넉넉한 수직 여백 · 얇은 대형 헤드라인 · 절제된 회색 본문 · 미니멀 카드 · 세련된 라인 아이콘.
//    ★제외(사용자): 사선(diagonal)·인물·제품이미지/그래픽(필요시 추후). 지금은 텍스트+라인아이콘+여백 중심.
//    섹션/콘텐츠/링크는 기존(기획서 v7 3-1) 유지, 테마만 다크→화이트로 교체. 제품 다크 모듈과 분리(SafetyEnv.module).
//  기존 구조(3-1): ①문제제시 ②솔루션구조 ③안전분야 ④핵심기능 ⑤도입사례 ⑥행동.
// ============================================================================

const HERO_IMG = '/images/hero/hero-safety.jpg';
const SLOGAN = '현장 위험을 실시간 측정하고 위험 알림·자동 기록·법정 보고서까지 하나로 연결해, 측정에서 관리·증빙까지 책임지는 산업안전 IoT 솔루션입니다.';

const Ic = ({ n }: { n: string }) => <div className={w.iconWrap}>{GUIDE_ICON[n]}</div>;
const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};

export default function SafetyLanding() {
  const navigate = useNavigate();
  const nav = (to: string) => navigate(to);
  const safetyCases = CASE_POSTS.filter((p) => p.domain === 'safety').slice(0, 3);

  return (
    <main className={w.page}>
      <PageLoader />

      <header className={w.hero}>
        <div className={w.heroBg} style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className={w.heroOverlay} />
        <HeroCrumbs items={['HOME', '안전', '안전 솔루션']} />
        <div className={w.heroInner}>
          <h1 className={w.heroTitle}>안전 솔루션</h1>
          <p className={w.heroSub}>{SLOGAN}</p>
        </div>
      </header>

      {/* ① 문제 제시 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>THE PROBLEM</p>
            <h2 className={w.h2}>현장의 안전, 사람의 눈과 손만으로는 한계가 있습니다</h2>
            <p className={w.lead}>현장마다 따로 측정하고, 수기로 기록하고, 전화로 연락하는 방식은 누락·지연·증빙 부재의 위험이 큽니다. EVS는 측정부터 알림·기록·보고까지 하나로 자동화합니다.</p>
            <a className={w.moreLink} onClick={() => nav('/safety/heat')}>온열질환 예방 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ② 솔루션 구조 — [2026-10-23 사용자] 데이터 여정 카드(이미지45 Clear Data Slides 재해석): 전폭 다크 네이비 밴드 + 블루 그라데이션 카드. 색=이미지 샘플 */}
      <section className={`${w.section} ${w.jband}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>HOW IT WORKS</p>
            <h2 className={w.h2}>단말에서 대시보드까지 하나로 연결</h2>
            <div className={w.jgrid}>
              {[
                { n: '01', t: '단말', i: 'monitor', d: '현장 IoT 단말이 체감온도·가스 등 위험을 실시간 측정합니다.', c: 'linear-gradient(160deg,#0B2468,#071A4E)' },
                { n: '02', t: '통신', i: 'signal', d: 'WiFi·Ethernet·LTE로 측정값을 클라우드에 전송합니다.', c: 'linear-gradient(160deg,#0F2E8E,#0A2468)' },
                { n: '03', t: '클라우드', i: 'database', d: '위험단계를 자동 판정하고 기록·보고서를 생성합니다.', c: 'linear-gradient(160deg,#143FB0,#0E2E86)' },
                { n: '04', t: '대시보드·TV·앱', i: 'phone', d: '관리자 대시보드·현장 TV·모바일 앱으로 알립니다.', c: 'linear-gradient(160deg,#1B4FD0,#123BA0)' },
              ].map((x) => (
                <div key={x.n} className={w.jcard} style={{ ['--jc' as string]: x.c } as React.CSSProperties}>
                  <span className={w.jcardNum}>{x.n}</span>
                  <div className={w.jcardTitle}>{x.t}</div>
                  <div className={w.jcardLine} />
                  <div className={w.jcardDesc}>{x.d}</div>
                  <div className={w.jcardIco}>{GUIDE_ICON[x.i]}</div>
                </div>
              ))}
            </div>
            <a className={w.moreLink} onClick={() => nav('/safety/platform')}>플랫폼 상세 →</a>
          </Reveal>
        </div>
      </section>

      {/* ③ 안전 분야 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>SAFETY FIELDS</p>
            <h2 className={w.h2}>안전 분야</h2>
            <div className={`${w.grid} ${w.grid3}`}>
              <a className={`${w.card} ${w.cCoral} ${w.fill}`} onClick={() => nav('/safety/heat')}>
                <Ic n="ai" /><h3>온열질환 예방</h3><p>체감온도(WBGT)를 실시간 관제해 폭염·온열질환 위험을 4단계로 대응합니다.</p>
              </a>
              <a className={`${w.card} ${w.cSky} ${w.fill}`} onClick={() => nav('/safety/cold')}>
                <Ic n="gauge" /><h3>한랭작업 관리<span className={w.soonTag}>(예정)</span></h3><p>온도·습도·풍속 기반 한랭작업 위험관리를 준비하고 있습니다.</p>
              </a>
              <a className={`${w.card} ${w.cTeal} ${w.fill}`} onClick={() => nav('/safety/gas')}>
                <Ic n="air" /><h3>밀폐공간 가스안전<span className={w.soonTag}>(예정)</span></h3><p>밀폐공간 유해가스 실시간 감지·경보 단말을 개발 중입니다.</p>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ④ 핵심 기능 */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>KEY FEATURES</p>
            <h2 className={w.h2}>핵심 기능</h2>
            <div className={`${w.grid} ${w.grid4}`}>
              <div className={`${w.card} ${w.cBlue} ${w.feat}`}><Ic n="gauge" /><h3>실시간 측정</h3><p>장비별 측정값·통신상태를 실시간 모니터링합니다.</p></div>
              <div className={`${w.card} ${w.cAmber} ${w.feat}`}><Ic n="bell" /><h3>위험 알림</h3><p>SMS·카카오·앱·이메일로 위험 단계를 즉시 전달합니다.</p></div>
              <div className={`${w.card} ${w.cIndigo} ${w.feat}`}><Ic n="toggle" /><h3>권한·로그</h3><p>역할별 권한과 설정·경보·작업 이력을 관리합니다.</p></div>
              <div className={`${w.card} ${w.cTeal} ${w.feat}`}><Ic n="doc" /><h3>보고서</h3><p>월간 보고서를 자동 생성해 법정기록·증빙을 지원합니다.</p></div>
            </div>
            <a className={w.moreLink} onClick={() => nav('/safety/platform')}>플랫폼 상세 →</a>
          </Reveal>
        </div>
      </section>

      {/* ⑤ 도입사례 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>CASES</p>
            <h2 className={w.h2}>도입사례</h2>
            <div className={w.caseGrid}>
              {safetyCases.map((c) => (
                <a key={c.slug} className={w.caseCard} onClick={() => nav(`/cases/${c.slug}`)}>
                  <div className={w.caseImg}><img src={c.cover} alt={c.title} loading="lazy" /></div>
                  <strong>{c.title}</strong><em>{c.date}</em>
                </a>
              ))}
            </div>
            <a className={w.moreLink} onClick={() => nav('/cases?tab=safety')}>안전 사례 전체 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ⑥ 행동 — [2026-10-11 사용자] 다크 밴드 취소(하단 다크 푸터와 붙어 구분 안 됨) → 라이트 유지 */}
      <section className={w.finalCta}>
        <div className={w.inner}>
          <Reveal>
            <h2 className={w.h2}>현장 안전, 지금 시작하세요</h2>
            <p className={w.lead}>통합관제 데모를 체험하거나 도입 상담을 받아보세요.</p>
            <div className={w.ctaRow}>
              <Button variant="pill" onClick={() => nav('/safety/platform')}>데모 체험</Button>
              <a className={w.ctaGhost} onClick={() => nav('/support/consult')}>도입 상담</a>
              <a className={w.ctaGhost} onClick={() => nav('/products')}>제품 보기</a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
